import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Loadable from 'react-loadable';
import { Container, Row, Col, ScreenClassProvider } from './grid';
import Logo from './components/atoms/logo';
import { PortalContainer } from './components/atoms/portal-container';

import LoginPage from './components/pages/login';
import SSOStatusPage from './components/pages/sso';
import ResetPage from './components/pages/reset';
import LoadingPage from './components/pages/loading';
import RegisterPage from './components/pages/register';
import AlertPage from './components/pages/alert';
import PatientNotFoundPage from './components/pages/patient/not_found';
import PatientChatPage from './components/pages/chat/patient';
import NewUserPage from './components/pages/user/new';
import { AuthProvider, useAuth } from './contexts/auth';
import Topbar from './components/organisms/topbar';

import './index.scss';
import { ChatsProvider } from './contexts/chats';
import { ChatSocketProvider } from './contexts/chats/socket';
import { MetaDataProvider } from './contexts/metadata';

const rootElement = document.getElementById('root');
const App = Loadable({
  loader: () => import(/* webpackChunkName: "app" */ './app'),
  loading: LoadingPage
});

function onSubmitHover() {
  App.preload();
}

const LoggedOutUserContainer: React.FC = ({ children }) => {
  return (
    <Container fluid fullscreen bg="grey">
      <Row>
        <Col md={10} offset={{ md: 7 }}>
          <header className="text-center mt-100 mb-8">
            <Logo maxWidth={250} />
          </header>
          {children}
        </Col>
      </Row>
    </Container>
  );
};

const Index = () => {
  const { user } = useAuth();
  const isFixturePage =
    window.location.pathname === '/_fixture' ||
    window.location.pathname === '/_fixture/';
  if (!user) {
    return (
      <main>
        <Router>
          <Routes>
            <Route
              path="/alert/:alertId"
              element={
                <LoggedOutUserContainer>
                  <AlertPage />
                </LoggedOutUserContainer>
              }
            />
            <Route path="/patient_notfound" element={<PatientNotFoundPage />} />
            <Route
              path="/chat/pt"
              element={
                <ChatSocketProvider>
                  <ChatsProvider>
                    <PatientChatPage />
                  </ChatsProvider>
                </ChatSocketProvider>
              }
            />
            <Route
              path="/user/new"
              element={
                <LoggedOutUserContainer>
                  <NewUserPage />
                </LoggedOutUserContainer>
              }
            />
            <Route
              path="/sso"
              element={
                <LoggedOutUserContainer>
                  <SSOStatusPage />
                </LoggedOutUserContainer>
              }
            />
            <Route
              path="/reset"
              element={
                <LoggedOutUserContainer>
                  <ResetPage />
                </LoggedOutUserContainer>
              }
            />
            <Route
              path="/register"
              element={
                <LoggedOutUserContainer>
                  <RegisterPage />
                </LoggedOutUserContainer>
              }
            />
            <Route
              path="*"
              element={
                <LoggedOutUserContainer>
                  <LoginPage onSubmitHover={onSubmitHover} />
                </LoggedOutUserContainer>
              }
            />
          </Routes>
        </Router>
        <PortalContainer />
      </main>
    );
  }
  if (isFixturePage) {
    return <App user={user} />;
  }

  return (
    <MetaDataProvider>
      <App
        user={user}
        topBar={<Topbar isAdminUser={user ? user.isAdmin : false} />}
      />
    </MetaDataProvider>
  );
};

const render = () => {
  ReactDOM.render(
    <ScreenClassProvider>
      <AuthProvider>
        <Index />
      </AuthProvider>
    </ScreenClassProvider>,
    rootElement
  );
};

render();
